import { useEffect, useState } from "react";
import { useDocumentTitle } from "../a11y";
import { api } from "../api";
import { Modal } from "../components/Dialogs";
import { playTimeNote, scenarioWeeksNote } from "../results";
import { groupScenarios, groupSummaryNote, startsOpen } from "../scenarioGroups";
import type { RunMode, RunSummary, ScenarioSummary, User } from "../types";

/** The firm warning before a quick-play run (06 Quick play). It must be confirmed. */
export const QUICK_PLAY_WARNING =
  "Quick play hands routine decisions to the firm's usual way of doing things. That changes the outcome, usually for the worse, " +
  "and your results will say less about how you lead. Quick-play results are marked as such and kept separate from full play.";

const STARTED = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

function startedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : STARTED.format(d);
}

/** Scenario select with resume (19 §4.12). The card states setting and length, never the lesson. Any scenario can be chosen. */
export function Start({ user, onOpenRun, onLogout }: { user: User; onOpenRun: (runId: string) => void; onLogout: () => void }) {
  const [scenarios, setScenarios] = useState<ScenarioSummary[] | null>(null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The run whose abandon is being confirmed. */
  const [confirming, setConfirming] = useState<string | null>(null);
  /** The scenario whose quick-play warning is open. */
  const [quickFor, setQuickFor] = useState<ScenarioSummary | null>(null);
  useDocumentTitle("Choose a scenario");

  useEffect(() => {
    Promise.all([api.scenarios(), api.runs()])
      .then(([s, r]) => {
        setScenarios(s.scenarios);
        setRuns(r.runs);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Could not load scenarios."));
  }, []);

  const abandon = async (runId: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.abandonRun(runId);
      setRuns((rs) => rs.map((r) => (r.id === runId ? { ...r, status: "abandoned" } : r)));
      setConfirming(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not abandon the run.");
    } finally {
      setBusy(false);
    }
  };

  const begin = async (scenarioId: string, mode: RunMode = "full") => {
    setBusy(true);
    setError(null);
    try {
      onOpenRun((await api.startRun(scenarioId, mode)).runId);
    } catch (e) {
      setQuickFor(null);
      setError(e instanceof Error ? e.message : "Could not start the run.");
      setBusy(false);
    }
  };

  return (
    <main className="entry entry--wide">
      <div className="entry__bar">
        <span>Signed in as {user.displayName}{user.company ? `, ${user.company}` : ""}</span>
        {/* Ends the session: there are no passwords, so this is how a shared device changes hands. */}
        <button type="button" className="btn btn--quiet" onClick={onLogout}>Not you? Sign up as someone else</button>
      </div>
      <h1 className="entry__title">Choose a scenario</h1>
      {error && <p className="form__error" role="alert">{error}</p>}
      {!scenarios ? (
        <p role="status">Loading…</p>
      ) : (
        scenarios.length === 0 ? (
          <p className="muted">No scenarios are available.</p>
        ) : (
          groupScenarios(scenarios, runs).map((g) => (
            <section key={g.id} className="scenario-group" aria-labelledby={`grp-${g.id}`}>
              <details open={startsOpen(g)}>
                <summary>
                  <h2 id={`grp-${g.id}`} className="scenario-group__title">{g.label}</h2>
                  <span className="muted scenario-group__note">{groupSummaryNote(g)}</span>
                </summary>
                <p className="muted scenario-group__blurb">{g.blurb}</p>
                <ul className="scenario-list">
                  {g.scenarios.map((s) => {
                  const weeks = scenarioWeeksNote(s);
                  const fullTime = playTimeNote(s, "full");
                  const quickTime = playTimeNote(s, "quick");
                  const mine = runs.filter((r) => r.scenarioId === s.id).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
                  return (
                    <li key={s.id} className="scenario-card">
                      <h3 id={`sc-${s.id}`}>{s.title}</h3>
                      {s.seat && <p className="scenario-card__seat"><span className="muted">You play:</span> {s.seat}</p>}
                      {weeks && <p className="muted">{weeks}</p>}
                      {/* Full play is the default; quick play asks for a confirmed warning first (06 Quick play). */}
                      <div className="row-actions play-choice" role="group" aria-labelledby={`begin-${s.id} sc-${s.id}`}>
                        <p className="play-choice__label" id={`begin-${s.id}`}>Begin a new run</p>
                        <button type="button" className="btn btn--primary" disabled={busy} aria-describedby={`sc-${s.id}`}
                          onClick={() => void begin(s.id, "full")}>Full play{fullTime ? `: ${fullTime}` : ""}</button>
                        <button type="button" className="btn" disabled={busy} aria-describedby={`sc-${s.id}`} aria-haspopup="dialog"
                          onClick={() => setQuickFor(s)}>Quick play{quickTime ? `: ${quickTime}` : ""}</button>
                      </div>
                      {mine.length > 0 && (
                        <div className="runs">
                          <h4>Your runs</h4>
                          <ul>
                            {mine.map((r) => (
                              <li key={r.id}>
                                <span id={`run-${r.id}`}>
                                  Started {startedAt(r.startedAt)}{r.status !== "active" ? ` · ${r.status}` : ""}{r.scoringRevealed ? " · scoring revealed" : ""}
                                  {r.mode === "quick" && <> · <span className="mode-tag">Quick play</span></>}
                                </span>
                                {r.status !== "abandoned" && (
                                  <button type="button" className="btn" onClick={() => onOpenRun(r.id)} aria-describedby={`run-${r.id} sc-${s.id}`}>
                                    {r.status === "active" ? "Continue" : "Look back"}
                                  </button>
                                )}
                                {r.status === "active" && confirming !== r.id && (
                                  <button type="button" className="btn btn--quiet" disabled={busy} onClick={() => setConfirming(r.id)}
                                    aria-describedby={`run-${r.id} sc-${s.id}`}>Abandon…</button>
                                )}
                                {confirming === r.id && (
                                  <span className="confirm-inline" role="group" aria-label="Abandon this run?">
                                    <span>Abandon this run? It can't be resumed.</span>
                                    <button type="button" className="btn" disabled={busy} autoFocus onClick={() => void abandon(r.id)}>Yes, abandon it</button>
                                    <button type="button" className="btn btn--quiet" onClick={() => setConfirming(null)}>Keep it</button>
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                  })}
                </ul>
              </details>
            </section>
          ))
        )
      )}
      <Modal title="Quick play changes your results" open={quickFor !== null} onClose={() => { if (!busy) setQuickFor(null); }}>
        <p>{QUICK_PLAY_WARNING}</p>
        <div className="row-actions">
          <button type="button" className="btn btn--primary" data-autofocus disabled={busy}
            onClick={() => { if (quickFor) void begin(quickFor.id, "full"); }}>Play the full version</button>
          <button type="button" className="btn" disabled={busy}
            onClick={() => { if (quickFor) void begin(quickFor.id, "quick"); }}>Start quick play</button>
        </div>
      </Modal>
    </main>
  );
}
